import React from "react";
import { LegalLayout, Section } from "@/components/legal/LegalLayout";

const P: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[color:var(--sg-fg)] leading-relaxed">{children}</p>
);
const UL: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="list-disc ml-6 space-y-1.5 text-[color:var(--sg-fg)]">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

export const PrivacyPolicy: React.FC = () => (
  <LegalLayout
    testId="page-privacy"
    title="Privacy Policy"
    subtitle="How SociBaba collects, uses, discloses and safeguards your information."
    lastUpdated="February 13, 2026"
    seoPath="/privacy"
    seoDescription="Read SociBaba's Privacy Policy: what data we collect, how we use it, GDPR/CCPA rights, and how we secure your workspace, OAuth tokens and media."
    seoKeywords={["SociBaba privacy policy","GDPR","CCPA","data protection","OAuth token security"]}
  >
    <P>
      SociBaba ("we", "our", "us") operates the SociBaba platform at https://www.socibaba.com. This Privacy Policy explains what data we collect, how we use it, who we share it with, and the choices and rights you have.
    </P>

    <Section n={1} title="Information we collect">
      <P>We collect the minimum data needed to operate the service:</P>
      <UL items={[
        "Account data: your name, email, hashed password, workspace, and (if you sign in with Google) your Google profile picture and Google user id.",
        "Content data: products, media assets (images/videos), captions, hashtags, publish jobs, comments you ingest, replies you draft, leads, complaints, and Discovery reports.",
        "OAuth tokens for connected platforms (YouTube, Facebook Pages, Instagram Business). Tokens are encrypted at rest with Fernet.",
        "Usage data: server logs (IP address, user-agent, timestamps, endpoints called) retained for security and debugging.",
        "Cookies: a session cookie for Google Auth and a bearer-token in localStorage for JWT-based sessions.",
      ]} />
    </Section>

    <Section n={2} title="How we use your information">
      <UL items={[
        "Provide the SociBaba platform: publish content, ingest comments, classify intent, generate reply drafts, run Discovery reports.",
        "Send account-related communications (verification, security notices).",
        "Improve product quality — aggregated, non-identifying analytics only.",
        "Comply with legal obligations.",
      ]} />
    </Section>

    <Section n={3} title="AI processing">
      <P>
        Text you submit — comment text, product descriptions, Discovery query text and mined public-conversation excerpts — is sent to Anthropic's Claude Sonnet 4.5 model via Emergent's LLM integration for classification and content generation. This data is processed transiently and is not used to train foundation models under our contract with the provider.
      </P>
    </Section>

    <Section n={4} title="Third-party platforms">
      <P>
        When you connect a YouTube, Facebook Page, or Instagram Business account, SociBaba acts on your behalf via the platform's official APIs using OAuth tokens you grant. We only request the minimum scopes needed to publish and read comments on your own content. We do not sell or share this access.
      </P>
    </Section>

    <Section n={5} title="Data retention">
      <UL items={[
        "Raw uploaded videos are automatically deleted from object storage after your publish job succeeds on all selected platforms.",
        "Thumbnails and metadata are retained for as long as your workspace is active.",
        "Server logs are retained for up to 30 days.",
        "On workspace deletion, all associated data is deleted within 30 days (see Data Deletion policy).",
      ]} />
    </Section>

    <Section n={6} title="Your rights">
      <P>You may access, correct, export, or delete your data at any time. Email <a className="text-[color:var(--sg-primary)] hover:underline" href="mailto:privacy@socibaba.com">privacy@socibaba.com</a> or visit <a className="text-[color:var(--sg-primary)] hover:underline" href="/data-deletion">Data Deletion</a>.</P>
    </Section>

    <Section n={7} title="Security">
      <P>
        Passwords are hashed with bcrypt. OAuth tokens are encrypted with Fernet keys. Traffic is served over HTTPS. Access to production data is restricted to authorised personnel and audit-logged.
      </P>
    </Section>

    <Section n={8} title="Children">
      <P>SociBaba is not intended for children under 13. We do not knowingly collect data from anyone under 13.</P>
    </Section>

    <Section n={9} title="Changes to this policy">
      <P>
        We may update this Privacy Policy from time to time. Material changes will be notified by email or in-product notice.
      </P>
    </Section>

    <Section n={10} title="Contact">
      <P>Questions? Reach us at <a className="text-[color:var(--sg-primary)] hover:underline" href="mailto:privacy@socibaba.com">privacy@socibaba.com</a>.</P>
    </Section>
  </LegalLayout>
);

export default PrivacyPolicy;
